import { cn } from '@/lib/utils';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { useEffect, useState } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { alertBannerQueryOptions } from '@/services/queryOptions/alertBannerQueryOptions';
import { HTMLContent } from '../ui/html-content';
import { AlertBanner } from '../ui/alert-banner';

interface AlertAnimationState {
  animations: {
    text: string;
    banner: string;
    closeButton: string;
  };
}

export const AppAlert = () => {
  const { data, isError } = useSuspenseQuery(alertBannerQueryOptions);

  const [animationState, setAnimationState] = useState<AlertAnimationState>({
    animations: {
      text: 'animate-banner-text-in',
      banner: 'px-4 py-[8px] md:p-3.5 lg:p-4',
      closeButton: '',
    },
  });

  const [isVisible, setIsVisible] = useState(true);
  const [currentAlert, setCurrentAlert] = useState(0);

  useEffect(() => {
    if (data.items.length === 0) {
      setIsVisible(false);
      return;
    }
    const cycleAlerts = setInterval(() => {
      setAnimationState((prev) => ({
        ...prev,
        animations: { ...prev.animations, text: 'animate-banner-text-out' },
      }));
      setTimeout(() => {
        setCurrentAlert((prev) => (prev + 1) % data.items.length);
        setAnimationState((prev) => ({
          ...prev,
          animations: { ...prev.animations, text: 'animate-banner-text-in' },
        }));
      }, 500);
    }, 5000);

    return () => clearInterval(cycleAlerts);
  }, [data]);

  const handleClose = () => {
    setAnimationState({
      animations: {
        text: 'hidden',
        banner: 'animate-banner-compress',
        closeButton: 'hidden',
      },
    });
    setTimeout(() => setIsVisible(false), 250);
  };

  if (!isVisible || data.items.length === 0 || isError) return null;
  return (
    <AlertBanner
      data-component="CMSAlert"
      className={cn(animationState.animations.banner)}
    >
      <div
        className={cn(
          'justify-between flex items-center',
          'text-left space-x-1.5 items-start md:text-center lg:text-center',
          animationState.animations.text
        )}
      >
        <HTMLContent
          content={data.items[currentAlert].content}
          className={cn(
            'flex-1',
            'text-[10px] md:text-base lg:text-base [&_p]:text-black [&_a]:underline [&_strong]:font-nunito700'
          )}
        />
        <button className="cursor-pointer" onClick={handleClose}>
          <XMarkIcon
            className={cn(
              'size-5 md:size-6 lg:size-6 text-black',
              animationState.animations.closeButton
            )}
          />
        </button>
      </div>
    </AlertBanner>
  );
};
